/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Utils
import { get, isEqual, isNil, isObject, isEmpty, isNumber } from 'lodash';

// Components
import AngularCustomControl from 'shared/ui/components/form-components/AngularCustomControl.vue';
import AnyExternalPicker from 'shared/ui/components/form-components/pickers/AnyExternalPicker.vue';
import Autogenerated from 'shared/ui/components/form-components/Autogenerated.vue';
import BaseInput from 'shared/ui/components/form-components/BaseInput.vue';
import BaseSelect from 'shared/ui/components/form-components/BaseSelect.vue';
import BaseTextarea from 'shared/ui/components/form-components/BaseTextarea.vue';
import Checkboxes from 'shared/ui/components/form-components/Checkboxes.vue';
import ContentPagePicker from 'shared/ui/components/form-components/pickers/ContentPagePicker.vue';
import ColorPicker from 'shared/ui/components/form-components/pickers/ColorPicker.vue';
import DatetimePicker from 'shared/ui/components/form-components/pickers/DatetimePicker.vue';
import EditorialContent from 'shared/ui/components/form-components/EditorialContent.vue';
import EmailTemplatePicker from 'shared/ui/components/form-components/pickers/EmailTemplatePicker.vue';
import FilePicker from 'shared/ui/components/form-components/pickers/FilePicker.vue';
import FpExtIdInput from 'shared/ui/components/form-components/FpExtIdInput.vue';
import GenericDocExternalPicker from 'shared/ui/components/form-components/pickers/GenericDocExternalPicker.vue';
import HiddenInput from 'shared/ui/components/form-components/HiddenInput.vue';
import HtmlEditor from 'shared/ui/components/editors/html/HtmlEditor.vue';
import ImagePicker from 'shared/ui/components/form-components/pickers/ImagePicker.vue';
import LocaleSelector from 'shared/ui/components/form-components/pickers/LocaleSelector.vue';
import MetaList from 'shared/ui/components/form-components/MetaList.vue';
import NestedControl from 'shared/ui/components/form-components/NestedControl.vue';
import RadioButtons from 'shared/ui/components/form-components/RadioButtons.vue';
import SecretsInput from 'shared/ui/components/form-components/SecretsInput.vue';
import TargetingControl from 'shared/ui/components/form-components/TargetingControl.vue';
import Toggler from 'shared/ui/components/form-components/Toggler.vue';
import VideoCallControl from 'shared/ui/components/form-components/VideoCallControl.vue';

// Constants
const KIND_TO_COMPONENT = {
    'choice-list': BaseSelect,
    'content-page': ContentPagePicker,
    'editorial': EditorialContent,
    'fp_ext_id': FpExtIdInput,
    'formatted-datetime': DatetimePicker,
    'nested-object': NestedControl,
    'targets-exceptions': TargetingControl,
    'text-multiline': BaseTextarea,
    'video-call': VideoCallControl,
    auto: Autogenerated,
    boolean: Toggler,
    choice: RadioButtons,
    colour: ColorPicker,
    external: GenericDocExternalPicker,
    file: FilePicker,
    hidden: HiddenInput,
    html: HtmlEditor,
    image: ImagePicker,
    language: LocaleSelector,
    locale: BaseSelect,
    list: MetaList,
    timestamp: DatetimePicker
};

/**
 * a value such as `{ fp_ext_id: null }` also returns true
 * @type {(d: any) => boolean}
 */
const isNilOrEmptyObject = (d) => isNil(d) || (isObject(d) && Object.values(d).every(isNil));

/**
 * This component is used to display an input control
 * based on its metadata kind.
 */
const __vue_script__ = {
    name: 'MetaControl',

    props: {
        settings: {
            type: Object,
            default: () => ({})
        },

        control: {
            type: Object,
            required: true
        },

        errors: {
            type: Array,
            default: () => []
        },

        statuses: {
            type: Object,
            default: () => ({})
        },

        value: {
            type: [Array, Object, Number, String, Boolean, Date],
            default: null
        },

        context: {
            type: String,
            default: null
        },

        disabled: {
            type: Boolean,
            default: null // Not an error, this is a special case
        },

        event: {
            type: Object,
            default: () => ({})
        },

        noLabel: {
            type: Boolean,
            default: false
        },

        noTip: {
            type: Boolean,
            default: false
        },

        edit: {
            type: Boolean,
            default: false
        },

        parentModel: {
            type: Object,
            default: () => ({})
        },

        name: {
            type: String,
            default: undefined
        },

        dependant: {
            type: Boolean,
            default: undefined
        },

        locale: {
            type: String,
            default: null
        },

        showHidden: {
            type: Boolean,
            default: false,
        }
    },

    data() {
        return {
            attrs: {},
            disabledForDependsOn: undefined,
            inputComponent: BaseInput,
            innerValue: {},
            watchersAppended: false,
            visible: false
        };
    },

    watch: {
        control: {
            immediate: true,
            deep: true,
            handler(control) {
                this.determineInputComponent(control);
                this.updateAttributes();
                this.appendWatchers();
                this.setVisibility();
            }
        },

        disabled() {
            this.updateAttributes();
        },

        edit() {
            this.updateAttributes();
        },

        parentModel: {
            deep: true,
            handler() {
                if (this.attrs.hasOwnProperty('model')) {
                    this.attrs.model = this.parentModel;
                }

                if (this.inputComponent === TargetingControl) {
                    this.innerValue = this.parentModel;
                }
            }
        },

        settings() {
            this.updateAttributes();
            this.setVisibility();
        },

        value: {
            immediate: true,
            handler(value) {
                this.innerValue = value;
            }
        }
    },

    created() {
        if (this.inputComponent === DatetimePicker && this.control.kind_options?.initialValue) {
            this.innerValue = this.value || get(this, this.control.kind_options.initialValue, this.value);
        } else if (this.control.kind === 'targets-exceptions') {
            // This is needed because "Target and Exceptions" control is a special case that
            // handles two fields at once: "targets" and "exceptions".
            this.innerValue = this.parentModel;
        } else if (this.control.default) {
            if (this.control.kind === 'timestamp' && isNil(this.value)) {
                // "extractDefaults" already set the value, but for timestamps we need to convert it to a number.
                this.innerValue = this.$utils.time.nowPlusDurationInUnixTs(this.control.default, this.event.timezone);
                // Because of `extractDefaults` though, the value isn't correct, so we need to emit the correct one.
                this.input(this.innerValue);
            } else if (typeof this.value === 'boolean' || !isNil(this.value)) { // Prevent the default value from being used when the value is `false`
                this.innerValue = this.value;
            } else {
                this.innerValue = this.control.default;
            }
        }
    },

    methods: {
        determineInputComponent(control) {
            let inputComponent;
            const options = get(control, 'kind_options', {});

            if (control.kind === 'custom') {
                inputComponent = this.$const.CUSTOM_KIND_COMPONENTS[options.component] || AngularCustomControl;

                if (!options.component) {
                    console.warn('[MetaControl] Custom kind declared without `component` key. Falling back to AngularCustomControl.', control);
                } else if (!this.$const.CUSTOM_KIND_COMPONENTS[options.component]) {
                    console.warn(`[MetaControl] Custom component "${options.component}" not found. Falling back to AngularCustomControl.`, control);
                }

            } else {
                inputComponent = KIND_TO_COMPONENT[control.kind] || BaseInput;

                if (options?.list_style === 'select') {
                    inputComponent = BaseSelect;
                }

                if (options?.list_style === 'multi-choice') {
                    inputComponent = Checkboxes;
                }

                if (options?.type === 'mail_template') {
                    inputComponent = EmailTemplatePicker;
                }

                if (options?.type === 'any' && inputComponent === GenericDocExternalPicker) {
                    inputComponent = AnyExternalPicker;
                }

                if (options?.type === 'secrets_field') {
                    inputComponent = SecretsInput;
                }
            }

            this.inputComponent = inputComponent;
        },

        updateAttributes() {
            const attrs = this.$utils.controls.getControlAttributes(this.control, {
                $i18n: this.$i18n,
                context: this.context,
                event: this.event,
                noLabel: this.noLabel || this.control.noLabel || this.control.no_label,
                noTip: this.noTip || this.control.noTip || this.control.no_tip,
                hintAfterLabel: this.control.hintAfterLabel || this.control.hint_after_label,
                parentModel: this.parentModel,
                settings: this.settings,
                value: this.value,
                locale: this.locale
            }, {
                edit: this.edit,
                dependant: this.dependant,
                disabled: (this.disabled === null ? this.control.disabled : this.disabled)
                    || this.disabledForDependsOn,
            });

            if (this.name?.length) {
                attrs.name = this.name;
            }

            if (this.inputComponent === BaseSelect) {
                attrs.emitOptionValueOnly = !isObject(this.value) && isObject(attrs.options?.[0]);
                if (get(this, 'control.kind_options.list_style') === 'multi-choice' || this.control.kind_options?.emitOptionValueOnly) {
                    attrs.emitOptionValueOnly = true;
                }
            }

            if (this.control.kind === 'fp_ext_id') {
                attrs.eid = this.control.eid;
                attrs.fpType = this.control.fpType;
            }

            if (this.control.kind_options?.exclude_self === true) {
                attrs.exclusions = [...(attrs.exclusions ?? []), this.parentModel.fp_ext_id];
            }

            this.attrs = this.reconcileOptionsAndProps(attrs);
        },

        /**
         * Appends watchers to the component.
         * This method is responsible for adding watchers to the component's reactive properties.
         * It ensures that any changes to these properties are observed and handled accordingly.
         */
        appendWatchers() {
            if (this.watchersAppended) return;

            if (this.control.exclusive_with) {
                for (const field of this.control.exclusive_with) {
                    this.$watch(`parentModel.${field}`, (newValue, oldValue) => {
                        if (!isEqual(newValue, oldValue) && !isNil(newValue)) {
                            return this.exclusivityHandler(field, newValue, oldValue);
                        }
                    }, { deep: true });
                }
            }

            if (this.control.depends_on) {
                // "depends_on" means the control is disabled
                // when the value of the key it depends on is nil
                for (const field of Object.keys(this.control.depends_on)) {
                    this.$watch(`parentModel.${field}`, newValue => {
                        this.disabledForDependsOn = isNilOrEmptyObject(newValue);
                        this.updateAttributes();
                    }, { deep: true, immediate: true });
                }
            }

            if (this.control.visibility) {
                this.$watch('parentModel', this.setVisibility, { deep: true });
            }

            if (this.inputComponent === DatetimePicker) {
                if (this.control.kind_options?.not_before) {
                    this.$watch(`parentModel.${this.control.kind_options.not_before}`, () => this.updateAttributes());
                }

                if (this.control.kind_options?.not_after) {
                    this.$watch(`parentModel.${this.control.kind_options.not_after}`, () => this.updateAttributes());
                }
            }

            this.watchersAppended = true;
        },

        /**
         * Handles the exclusivity logic for a form field.
         *
         * @param {Object} field - The form field object.
         * @param {*} newVal - The new value of the field.
         * @param {*} oldVal - The old value of the field.
         */
        exclusivityHandler(field, newVal, oldVal) {
            if (newVal || Number.isFinite(newVal)) {
                console.debug('[MetaControl] Exclusivity:', this.control.field, 'reacting to', field, newVal, oldVal);
                this.input(null);
            }
        },

        /**
         * Triggered when user interacts with input components.
         *
         * @param {*} keyOrValue the value of the input
         * @param {*} [value] the value of the input
         */
        input(keyOrValue, value) {
            // Special case for base select
            if (this.inputComponent === BaseSelect) {
                if (keyOrValue?.value) {
                    keyOrValue = keyOrValue.value;
                }
            }

            // when kind is timestamp and DateTimePicker returns a stringified integer
            if (this.control.kind === 'timestamp' && !isNumber(keyOrValue) && Number.isInteger(Number(keyOrValue))) {
                keyOrValue = Number(keyOrValue);
            }

            if (this.inputComponent === AngularCustomControl) {
                // Angular components can emit more than just a single
                // field value. Sometimes they alter different parts
                // of the model.

                // When this happens we need to handle it differently.
                this.$emit('multi-input', keyOrValue, value);

            } else if (this.inputComponent === TargetingControl) {
                // TargetingControl emits two values: targets and exceptions
                for (const [key, value] of Object.entries(keyOrValue)) {
                    this.$emit('multi-input', key, value);
                }
            } else if (value !== undefined) {
                this.$emit('multi-input', keyOrValue, value);
            }
            else {
                this.$emit('input', keyOrValue);
            }
        },

        /**
         * Handler triggered on context updates
         *
         * @param {*} value the value of the context update
         */
        update(value) {
            this.$emit('rev', value);
        },

        /**
         * Reconciles the options and props of the component.
         *
         * This method is responsible for synchronizing the options and props
         * to ensure that they are in a consistent state.
         *
         * This method makes possible to have `kind_options` mirroring components
         * props, so that the user can pass props directly to the component from
         * the metadata `kind_options`.
         *
         * @param {Object} attrs the attributes object
         *
         * @returns {Object} the reconciled attributes
         */
        reconcileOptionsAndProps(attrs) {
            if (!attrs.options || isEmpty(this.inputComponent.props)) return attrs;

            const componentProps = Object.keys(this.inputComponent.props);
            for (const [name, value] of Object.entries(attrs.options)) {
                if (componentProps.includes(name)) {
                    attrs[name] = value;
                }
            }

            if (isEmpty(attrs.options)) {
                delete attrs.options;
            }

            if (this.inputComponent === AngularCustomControl) {
                if (Array.isArray(attrs.options)) {
                    const opts = {};
                    attrs.options.forEach(option => {
                        if (option?.hasOwnProperty('label') && option?.hasOwnProperty('value')) {
                            opts[option.value] = option.label;
                        }
                    });

                    attrs.options = opts;
                }
            }

            return attrs;
        },

        /**
         * Checks the visibility of the component based on certain conditions.
         * This method determines whether the component should be visible or hidden.
         */
        async setVisibility() {
            try {
                if (this.control.permission) {
                    const org_id = event.bs_owner_id || event.ownerId;
                    const context = { event_id: this.event._id, org_id };
                    this.visibility = this.$services.user.hasPermission(this.control.permission, context);
                    return;
                }

                if (!this.control.visibility) {
                    this.visible = this.control.kind !== 'hidden' || this.showHidden === true;
                    return;
                }

                const { data_source, rules } = this.control.visibility;
                const { protocol, host, pathname } = new URL(data_source);
                const path = host || pathname.replace(/^\//, '');

                let data = null;
                if (protocol === 'settings:') {
                    data = get(this.settings, path);
                } else if (protocol === 'documents:') {
                    const doc = await this.$services.documents.getDocById(this.event._id, host);
                    data = get(doc, path);
                } else if (protocol === 'metadata:') {
                    const meta = await this.$services.metadata.getMetadataForType(this.event._id, host);
                    data = get(meta, path);
                } else if (protocol === 'model:') {
                    data = path === '.' ? this.parentModel : get(this.parentModel, path);
                } else {
                    console.error('[MetaControl] Unsupported visibility data source:', data_source);
                    return;
                }

                this.visible = this.$utils.objects.areConditionsMet(rules, data);
            } catch (error) {
                console.error('[MetaControl] Error checking visibility:', error);
                this.visible = this.control.visibility?.fallback || false;
            }
        },
    },
};

/* template */
var __vue_render__ = function () {
  var _vm = this
  var _h = _vm.$createElement
  var _c = _vm._self._c || _h
  return _vm.visible
    ? _c(
        _vm.inputComponent,
        _vm._b(
          {
            tag: "component",
            attrs: { errors: _vm.errors, statuses: _vm.statuses },
            on: {
              behaviour: function (b) {
                return _vm.$emit("behaviour", b)
              },
              change: _vm.input,
              disable: function ($event) {
                return _vm.$emit("disable", $event)
              },
              input: _vm.input,
              reload: function ($event) {
                return _vm.$emit("reload", $event)
              },
              rev: _vm.update,
              "locale-change": function ($event) {
                return _vm.$emit("locale-change", $event)
              },
              save: function ($event) {
                return _vm.$emit("save", $event)
              },
            },
            scopedSlots: _vm._u(
              [
                {
                  key: "inside-label",
                  fn: function (ref) {
                    return [_vm._t("inside-label")]
                  },
                },
              ],
              null,
              true
            ),
            model: {
              value: _vm.innerValue,
              callback: function ($$v) {
                _vm.innerValue = $$v
              },
              expression: "innerValue",
            },
          },
          "component",
          Object.assign({}, _vm.$attrs, _vm.attrs),
          false
        )
      )
    : _vm._e()
}
var __vue_staticRenderFns__ = []
__vue_render__._withStripped = true

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "apps/shared/src/ui/components/form-components/MetaControl.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__